import { readWhatsAppConsent } from './whatsappEnquiry.js';

export const PURCHASE_CONTEXT_KEY = 'printee:purchase-context';
export const PURCHASE_CONTEXT_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const queuedByBrowser = new WeakMap();

function text(value) {
  return typeof value === 'string' && value.trim() && value.length <= 300
    && !/[\u0000-\u001f\u007f]/.test(value) ? value : undefined;
}

function money(value) {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? Number(value.toFixed(2)) : undefined;
}

function purchasePhone(value) {
  const compact = value.trim().replace(/[\s().-]/g, '');
  // The owner explicitly chose Israel (+972) for local purchase phone numbers.
  if (/^0(?:[57]\d{8}|[23489]\d{7})$/.test(compact)) {
    return `+972${compact.slice(1)}`;
  }
  if (/^(?:00)?972(?:[57]\d{8}|[23489]\d{7})$/.test(compact)) {
    return `+${compact.replace(/^00/, '')}`;
  }
  if (/^\+[1-9]\d{10,14}$/.test(compact)) return compact;
  // Preserve unsupported input rather than inventing missing phone digits.
  return value;
}

function customerFields(contact = {}) {
  const customer = {};
  for (const key of ['name', 'email', 'phone', 'first_name', 'last_name']) {
    if (text(contact[key])) customer[key] = key === 'phone' ? purchasePhone(contact[key]) : contact[key];
  }
  const address = {};
  for (const key of ['street', 'city', 'region', 'postal_code', 'country']) {
    const value = contact[key] ?? contact[`address_${key}`];
    if (text(value)) address[key] = value;
  }
  if (Object.keys(address).length) customer.address = address;
  return customer;
}

function purchaseItems(items) {
  if (!Array.isArray(items)) return [];
  const result = [];
  for (const item of items) {
    if ((!text(item?.item_id) && !text(item?.item_name))
      || !Number.isSafeInteger(item?.quantity) || item.quantity <= 0) return [];
    const next = { quantity: item.quantity };
    for (const key of ['item_id', 'item_name', 'item_variant', 'coupon']) {
      if (text(item[key])) next[key] = item[key];
    }
    for (const key of ['price', 'discount']) {
      if (money(item[key]) !== undefined) next[key] = money(item[key]);
    }
    result.push(next);
  }
  return result;
}

function buildPurchaseContext({
  browser = globalThis.window, idempotencyKey, contact, items, financial, now = Date.now(),
} = {}) {
  const amount = money(financial?.total_after_payment_adjustments);
  if (!text(idempotencyKey) || amount === undefined || amount <= 0) return null;
  const consent = readWhatsAppConsent(browser?.dataLayer);
  return {
    version: 1,
    idempotency_key: idempotencyKey,
    captured_at: now,
    amount,
    currency: 'ILS',
    customer: consent.ad_user_data === 'denied' ? {} : customerFields(contact),
    items: purchaseItems(items),
    tax: money(financial.vat),
    shipping: money(financial.delivery),
    coupon: text(financial.coupon_code),
    card_discount: money(financial.card_discount),
    coupon_discount: money(financial.coupon_discount),
  };
}

// Capture only the fields GTM needs, before leaving the site for payment.
// This snapshot supplements a verified server confirmation; it cannot prove payment.
export function savePurchaseContext(options = {}) {
  const browser = options.browser ?? globalThis.window;
  const context = buildPurchaseContext(options);
  if (!context) return false;
  try {
    browser.sessionStorage.setItem(PURCHASE_CONTEXT_KEY, JSON.stringify(context));
    return true;
  } catch (_) {
    return false;
  }
}

function readPurchaseContext(browser, confirmation, now) {
  try {
    const context = JSON.parse(browser.sessionStorage.getItem(PURCHASE_CONTEXT_KEY));
    if (context?.version !== 1 || !Number.isFinite(context.captured_at)
      || now < context.captured_at || now - context.captured_at >= PURCHASE_CONTEXT_MAX_AGE_MS) {
      browser.sessionStorage.removeItem(PURCHASE_CONTEXT_KEY);
      return null;
    }
    if (context.idempotency_key !== confirmation.idempotency_key
      || context.currency !== confirmation.currency
      || context.amount !== confirmation.value) return null;
    return context;
  } catch (_) {
    return null;
  }
}

export function isConfirmedCardPurchase(confirmation) {
  return confirmation?.confirmed === true && confirmation.payment_method === 'card'
    && Boolean(text(confirmation.transaction_id)) && Boolean(text(confirmation.idempotency_key))
    && confirmation.currency === 'ILS' && money(confirmation.value) > 0;
}

function enhancedConversionFields(customer) {
  const userData = {};
  if (customer.email) userData.email = customer.email;
  // customerFields also normalizes snapshots captured before this release.
  if (/^\+[1-9]\d{10,14}$/.test(customer.phone || '')) userData.phone_number = customer.phone;
  const address = { ...(customer.address || {}) };
  if (customer.first_name) address.first_name = customer.first_name;
  if (customer.last_name) address.last_name = customer.last_name;
  if (Object.keys(address).length) userData.address = address;
  return Object.keys(userData).length ? userData : null;
}

export function queueConfirmedPurchase({
  browser = globalThis.window, confirmation, now = Date.now(),
} = {}) {
  if (!browser || !isConfirmedCardPurchase(confirmation)) {
    return { queued: false, reason: 'unconfirmed_purchase' };
  }
  return queuePurchase({ browser, confirmation, now });
}

// The owner explicitly chose purchase on successful Bit order submission.
// This is an order event, not evidence that the transfer was received.
export function queueSubmittedBitPurchase({
  browser = globalThis.window, submission, contact, items, financial, now = Date.now(),
} = {}) {
  if (!browser || submission?.submitted !== true || submission.payment_method !== 'bit'
    || !text(submission.transaction_id) || !text(submission.idempotency_key)
    || submission.currency !== 'ILS' || !(money(submission.value) > 0)) {
    return { queued: false, reason: 'unsubmitted_purchase' };
  }
  const context = buildPurchaseContext({
    browser, idempotencyKey: submission.idempotency_key, contact, items, financial, now,
  });
  if (!context || context.amount !== submission.value) {
    return { queued: false, reason: 'invalid_submission_context' };
  }
  return queuePurchase({ browser, confirmation: submission, submittedContext: context, now });
}

function queuePurchase({ browser, confirmation, submittedContext, now }) {
  const isBitSubmission = confirmation.payment_method === 'bit';
  const transactionId = confirmation.transaction_id;
  const guardKey = isBitSubmission
    ? `gtm_purchase_submitted_bit_${transactionId}` : `gtm_purchase_confirmed_${transactionId}`;
  let queued = queuedByBrowser.get(browser);
  if (!queued) {
    queued = new Set();
    queuedByBrowser.set(browser, queued);
  }
  let duplicate = queued.has(guardKey);
  try {
    duplicate ||= Boolean(browser.localStorage.getItem(guardKey)
      || (!isBitSubmission && browser.localStorage.getItem(`gtag_purchase_confirmed_${transactionId}`)));
  } catch (_) {}
  if (duplicate) {
    try {
      const context = JSON.parse(browser.sessionStorage.getItem(PURCHASE_CONTEXT_KEY));
      if (context?.idempotency_key === confirmation.idempotency_key) {
        browser.sessionStorage.removeItem(PURCHASE_CONTEXT_KEY);
      }
    } catch (_) {}
    return { queued: false, reason: 'already_queued' };
  }

  const context = submittedContext || readPurchaseContext(browser, confirmation, now);
  const consent = readWhatsAppConsent(browser.dataLayer);
  const customer = context && consent.ad_user_data !== 'denied'
    ? customerFields({ ...context.customer, ...context.customer?.address }) : {};
  const ecommerce = {
    transaction_id: transactionId,
    currency: confirmation.currency,
    // Card uses canonical paid value; Bit uses the submitted order's gross total.
    value: confirmation.value,
    items: purchaseItems(context?.items),
  };
  for (const key of ['tax', 'shipping']) {
    if (money(context?.[key]) !== undefined) ecommerce[key] = money(context[key]);
  }
  if (text(context?.coupon)) ecommerce.coupon = context.coupon;
  const payload = {
    event: 'purchase',
    payment_type: confirmation.payment_method,
    purchase_confirmation_source: isBitSubmission
      ? 'checkout_bit_order_submitted' : confirmation.collection_provider === 'takbull'
        ? 'takbull_status_business_confirmed' : 'icount_status_business_confirmed',
    purchase_value_scope: isBitSubmission
      ? 'submitted_total_including_vat_shipping_after_payment_adjustments'
      : 'paid_total_including_vat_shipping_after_payment_adjustments',
    purchase_payment_confirmed: !isBitSubmission,
    purchase_item_price_scope: 'including_vat_after_merchandise_discount_before_payment_adjustments',
    purchase_context_available: Boolean(context),
    consent_state: consent,
    customer: Object.keys(customer).length ? customer : null,
    user_data: enhancedConversionFields(customer),
    ecommerce,
  };
  for (const key of ['card_discount', 'coupon_discount']) {
    if (money(context?.[key]) !== undefined) payload[key] = money(context[key]);
  }
  try {
    browser.dataLayer ||= [];
    // GTM version-2 variables merge objects; clear every optional object first.
    browser.dataLayer.push({ ecommerce: null, customer: null, user_data: null });
    browser.dataLayer.push(payload);
  } catch (_) {
    return { queued: false, reason: 'data_layer_exception' };
  }
  queued.add(guardKey);
  try { browser.localStorage.setItem(guardKey, '1'); } catch (_) {}
  // Clear the GTM model after its event, avoiding contact reuse on later events.
  try { browser.dataLayer.push({ customer: null, user_data: null }); } catch (_) {}
  if (context && !isBitSubmission) {
    try { browser.sessionStorage.removeItem(PURCHASE_CONTEXT_KEY); } catch (_) {}
  }
  return { queued: true };
}
